import { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Eye, EyeOff, Shield, Info } from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * User-facing "My Access" panel — every workspace member can open this from
 * Settings → My Access to see exactly which tabs they have access to in the
 * current workspace AND why (role default vs admin override).
 *
 * Transparency drives trust: instead of "where did Banking go?", users see
 * "Banking — hidden by your admin (was visible by default for Bookkeepers)"
 * and can ask their admin to flip it on with the right context.
 */
export default function MyAccessPanel() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAccess = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/my-access`);
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => { fetchAccess(); }, [fetchAccess]);

  const sectioned = useMemo(() => {
    if (!data?.visibility) return [];
    const map = new Map();
    for (const v of data.visibility) {
      if (!map.has(v.section)) map.set(v.section, []);
      map.get(v.section).push(v);
    }
    return Array.from(map.entries());
  }, [data]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading your access…</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Couldn't load access info.</p>;

  const visibleCount = data.visibility.filter((v) => v.effective_visible).length;
  const hiddenCount = data.visibility.length - visibleCount;

  return (
    <div className="space-y-4" data-testid="my-access-panel">
      <Card className="border-emerald-200/60 bg-emerald-50/30 dark:bg-emerald-950/15">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-sm">My Access</h3>
                <Badge variant="outline" className="capitalize text-[10px]">
                  Role: {data.role}
                </Badge>
                {data.applied_preset && (
                  <Badge variant="outline" className="text-[10px] bg-violet-50 dark:bg-violet-950/30 border-violet-200">
                    Preset: {data.applied_preset}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                You can see <strong>{visibleCount}</strong> {visibleCount === 1 ? 'tab' : 'tabs'} ·
                <strong> {hiddenCount}</strong> hidden. Need access to something hidden?
                Ask a workspace owner / admin to grant it from <em>Admin Panel → Access Controls</em>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {sectioned.map(([section, tabs]) => {
          const sectionVisible = tabs.filter((t) => t.effective_visible).length;
          return (
            <Card key={section} data-testid={`my-access-section-${section.toLowerCase()}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-baseline justify-between">
                  <h4 className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-mono">
                    {section}
                  </h4>
                  <span className="text-[10px] text-muted-foreground">
                    {sectionVisible} / {tabs.length} visible
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {tabs.map((t) => {
                    const adminHidden = t.override === false;
                    const adminShown = t.override === true;
                    return (
                      <div
                        key={t.id}
                        className={cn(
                          'flex items-start gap-2 px-3 py-2 rounded-md border text-sm',
                          t.effective_visible
                            ? 'bg-emerald-50/40 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-900/40'
                            : 'bg-zinc-50/40 border-zinc-200 dark:bg-zinc-900/20 dark:border-zinc-800/60 opacity-80',
                        )}
                        data-testid={`my-access-tab-${t.id}`}
                      >
                        {t.effective_visible ? (
                          <Eye className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{t.label}</div>
                          <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                            {t.description}
                          </div>
                          {(adminHidden || adminShown) && (
                            <div
                              className={cn(
                                'text-[10px] mt-1 inline-flex items-center gap-1 font-medium',
                                adminHidden ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-400',
                              )}
                            >
                              <Info className="w-3 h-3" />
                              {adminHidden ? 'Hidden by your admin' : 'Granted by your admin'}
                              {t.default_visible !== t.effective_visible && (
                                <span className="text-muted-foreground font-normal">
                                  {' '}(default for {data.role}: {t.default_visible ? 'visible' : 'hidden'})
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
